import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Field, Notice, PageShell } from "@/components/site/Layout";

const AuthCard = ({ title, children }) => (
  <PageShell narrow><div className="max-w-md mx-auto hn-card p-8 md:p-10"><h1 className="text-4xl mb-6">{title}</h1>{children}</div></PageShell>
);

const GoogleButton = () => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return <button type="button" onClick={go} className="hn-btn hn-btn-outline w-full" data-testid="google-signin-btn">Continue with Google</button>;
};

function useForm(init) {
  const [f, setF] = useState(init);
  return [f, (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value })];
}

export function Login() {
  const { setUser, refreshCart } = useStore();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, on] = useForm({ email: "", password: "" });
  const [st, setSt] = useState({});
  const submit = async (e) => {
    e.preventDefault();
    setSt({ loading: true });
    try {
      const { data } = await api.post("/auth/login", f);
      setUser(data);
      await refreshCart();
      nav(sp.get("next") || "/account");
    } catch (err) {
      setSt({ error: errMsg(err) });
    }
  };
  return (
    <AuthCard title="Sign in">
      <form onSubmit={submit} className="grid gap-4" data-testid="login-form">
        <Field label="Email" id="l-email"><input id="l-email" type="email" autoComplete="email" required className="hn-input" value={f.email} onChange={on("email")} data-testid="login-email-input" /></Field>
        <Field label="Password" id="l-pw"><input id="l-pw" type="password" autoComplete="current-password" required className="hn-input" value={f.password} onChange={on("password")} data-testid="login-password-input" /></Field>
        {st.error && <Notice tone="error" testid="login-error">{st.error}</Notice>}
        <button className="hn-btn" disabled={st.loading} data-testid="login-submit-btn">{st.loading ? "Signing in…" : "Sign in"}</button>
      </form>
      <div className="my-5 text-center text-xs text-[#5b6b62]">or</div>
      <GoogleButton />
      <div className="mt-6 flex justify-between text-sm">
        <Link to="/forgot-password" className="underline" data-testid="forgot-password-link">Forgot password?</Link>
        <Link to={`/register${sp.get("next") ? "?next=" + sp.get("next") : ""}`} className="underline" data-testid="register-link">Create account</Link>
      </div>
    </AuthCard>
  );
}

export function Register() {
  const { setUser, refreshCart } = useStore();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, on] = useForm({ name: "", email: "", password: "", marketing_opt_in: false });
  const [st, setSt] = useState({});
  const submit = async (e) => {
    e.preventDefault();
    setSt({ loading: true });
    try {
      const { data } = await api.post("/auth/register", f);
      setUser(data);
      await refreshCart();
      nav(sp.get("next") || "/account?welcome=1");
    } catch (err) {
      setSt({ error: errMsg(err) });
    }
  };
  return (
    <AuthCard title="Create account">
      <form onSubmit={submit} className="grid gap-4" data-testid="register-form">
        <Field label="Name" id="r-name"><input id="r-name" required autoComplete="name" className="hn-input" value={f.name} onChange={on("name")} data-testid="register-name-input" /></Field>
        <Field label="Email" id="r-email"><input id="r-email" type="email" required autoComplete="email" className="hn-input" value={f.email} onChange={on("email")} data-testid="register-email-input" /></Field>
        <Field label="Password" id="r-pw" hint="At least 10 characters, with letters and numbers."><input id="r-pw" type="password" required autoComplete="new-password" className="hn-input" value={f.password} onChange={on("password")} data-testid="register-password-input" /></Field>
        <label className="flex gap-3 text-sm"><input type="checkbox" className="accent-[#173B2D]" checked={f.marketing_opt_in} onChange={on("marketing_opt_in")} data-testid="register-marketing-checkbox" />Email me HONEY NUTS news (optional)</label>
        {st.error && <Notice tone="error" testid="register-error">{st.error}</Notice>}
        <button className="hn-btn" disabled={st.loading} data-testid="register-submit-btn">{st.loading ? "Creating…" : "Create account"}</button>
      </form>
      <div className="my-5 text-center text-xs text-[#5b6b62]">or</div>
      <GoogleButton />
      <p className="mt-6 text-sm">Already have an account? <Link to="/login" className="underline">Sign in</Link></p>
    </AuthCard>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [st, setSt] = useState({});
  const submit = async (e) => {
    e.preventDefault();
    setSt({ loading: true });
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSt({ done: data.message });
    } catch (err) {
      setSt({ error: errMsg(err) });
    }
  };
  return (
    <AuthCard title="Reset password">
      {st.done ? <Notice testid="forgot-success">{st.done}</Notice> : (
        <form onSubmit={submit} className="grid gap-4">
          <Field label="Email" id="f-email"><input id="f-email" type="email" required className="hn-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></Field>
          {st.error && <Notice tone="error">{st.error}</Notice>}
          <button className="hn-btn" disabled={st.loading} data-testid="forgot-submit-btn">{st.loading ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
    </AuthCard>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const [st, setSt] = useState({});
  const staff = sp.get("staff");
  const submit = async (e) => {
    e.preventDefault();
    setSt({ loading: true });
    try {
      await api.post("/auth/reset-password", { token: sp.get("token") || "", password: pw });
      setSt({ done: true });
    } catch (err) {
      setSt({ error: errMsg(err) });
    }
  };
  return (
    <AuthCard title={staff ? "Set your password" : "Choose a new password"}>
      {st.done ? <><Notice testid="reset-success">Your password has been saved.</Notice><Link to={staff ? "/admin/login" : "/login"} className="hn-btn mt-6">Sign in</Link></> : (
        <form onSubmit={submit} className="grid gap-4">
          <Field label="New password" id="rp" hint="At least 10 characters, with letters and numbers."><input id="rp" type="password" required autoComplete="new-password" className="hn-input" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></Field>
          {st.error && <Notice tone="error" testid="reset-error">{st.error}</Notice>}
          <button className="hn-btn" disabled={st.loading} data-testid="reset-submit-btn">{st.loading ? "Saving…" : "Save password"}</button>
        </form>
      )}
    </AuthCard>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const { refreshUser } = useStore();
  const [st, setSt] = useState(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    api.post("/auth/verify-email", { token: sp.get("token") || "" }).then((r) => { setSt({ ok: true, changed: r.data.email_changed }); refreshUser(); }).catch((e) => setSt({ error: errMsg(e) }));
  }, [sp, refreshUser]);
  return (
    <AuthCard title="Email verification">
      {!st ? <p>Verifying…</p> : st.ok ? <Notice testid="verify-success">{st.changed ? "Your new email address is confirmed." : "Thank you — your email is verified."}</Notice> : <Notice tone="error" testid="verify-error">{st.error}</Notice>}
      <Link to="/account" className="hn-btn mt-6">Go to my account</Link>
    </AuthCard>
  );
}

export function AuthCallback() {
  const { setUser, refreshUser, refreshCart } = useStore();
  const nav = useNavigate();
  const done = useRef(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    window.history.replaceState(null, "", window.location.pathname);
    api.post("/auth/google", { session_id: sid }).then(async (r) => { setUser(r.data); await refreshCart(); nav("/account", { replace: true }); }).catch((e) => { setErr(errMsg(e)); refreshUser(); });
  }, [nav, setUser, refreshUser, refreshCart]);
  return err ? <AuthCard title="Sign in"><Notice tone="error">{err}</Notice><Link to="/login" className="hn-btn mt-6">Try again</Link></AuthCard> : null;
}
